import { DecisionEngineStatus } from "@hightouch/lib/customer-data/decision-engine/types";
import {
  type GroupByColumn,
  SYNTHETIC_COLUMN_TYPES,
} from "@hightouch/lib/query/visual/types";
import { subSeconds } from "date-fns";
import type { Flow } from "./types";
import { sortBy } from "lodash";

export const isFlowPaused = (flow: Flow) => {
  return flow.status === DecisionEngineStatus.READY && !flow.enabled;
};

export function showAgentOverviewTab(flow: Flow) {
  return !(
    (flow.status === DecisionEngineStatus.PENDING || isFlowPaused(flow)) &&
    !flow.config.demo_data
  );
}

export const ACTION_FEATURES_COLUMN_TYPE = SYNTHETIC_COLUMN_TYPES[2];

export type StartDateRangeInput =
  | {
      type: "custom";
      startDate: string | null | undefined;
    }
  | {
      type: "lookback";
      seconds: number;
    };

/**
 * Returns a date range tuple [startDate, endDate] based on either:
 * - A lookback period (e.g. last 7 days from end date)
 * - A specific start date
 */
export const getDateRangeForMetricSeries = (
  endDateString: string | null | undefined,
  start: StartDateRangeInput,
): [Date, Date] => {
  const now = new Date();
  const endDate = endDateString ? new Date(endDateString) : now;
  const end = getDateWithAddedTimezoneOffset(endDate);

  if (start.type === "lookback") {
    return [subSeconds(end, start.seconds), end];
  }

  const startDate = start.startDate
    ? getDateWithAddedTimezoneOffset(new Date(start.startDate))
    : now;

  return [startDate, end];
};

export const MESSAGE_GROUP_BY_COLUMN: GroupByColumn = {
  type: ACTION_FEATURES_COLUMN_TYPE,
  name: "message",
  alias: "Message",
};

/**
 * Adds the timezone offset to the date so we grab the UTC date when parsing
 * the timeWindow. (Especially important so we try to grab the cached data that
 * we queried from the latest flow run)
 */
export const getDateWithAddedTimezoneOffset = (date: Date) => {
  const timezoneOffset = date.getTimezoneOffset();
  date.setTime(date.getTime() + timezoneOffset * 60 * 1000);

  return date;
};

// order data by status (ready/enabled -> ready/paused -> training -> pending), then name
export const sortFlows = <T extends Pick<Flow, "status" | "enabled" | "name">>(
  flows: T[],
): T[] => {
  return sortBy(flows, [
    (o) => {
      switch (o.status) {
        case "ready":
          return o.enabled ? 0 : 1;
        case "training":
          return 2;
        case "pending":
          return 3;
        default:
          return -1;
      }
    },
    "name",
  ]);
};

/**
 * Checks if today is a blackout day based on the provided blackout days array
 * @param blackoutDays Array of date strings in ISO format
 * @returns true if today is a blackout day
 */
export const isBlackoutDay = (blackoutDays?: string[]): boolean => {
  if (!blackoutDays || blackoutDays.length === 0) {
    return false;
  }

  const today = new Date();
  const todayDateString = today.toISOString().split("T")[0]; // Get YYYY-MM-DD format

  return blackoutDays.some((blackoutDate) => {
    const blackoutDateString = new Date(blackoutDate)
      .toISOString()
      .split("T")[0];
    return blackoutDateString === todayDateString;
  });
};
